import { Stitch, RemoteMongoClient, AnonymousCredential, RemoteMongoCollection } from 'mongodb-stitch-browser-sdk'  
import { PROBLEM_FREQUENCY, MARKET_GROWTH, PRODUCT_COST, POPULARITY } from './common';
import { Text, idea as Idea, iResultAttribute, result } from './common';

import React, { useState, useEffect, SetStateAction, Dispatch } from 'react';
import { IdeaDetail } from './ideaDetail';
import { IdeasGrid } from './ideasGrid';
import amplitude from 'amplitude-js';


export const computeScore = (results: result[]) => {
    const average = (attribute: keyof result) => 
        results.reduce((d, {[attribute]: i}, _, l) => d + (i/l.length), 0
    )

    const computeAttribute = (attribute: keyof result, { options }: iResultAttribute) => {
        const avg = average(attribute)
        const maxValue = options.reduceRight(i => i).value
        const value = Math.log(avg)/Math.log(maxValue)
        return Math.max(value, 0)
    }

    const popularity = computeAttribute('popularity', POPULARITY);
    const market = computeAttribute('market', MARKET_GROWTH);
    const cost = computeAttribute('cost', PRODUCT_COST);

    const frequency = computeAttribute('frequency', PROBLEM_FREQUENCY);
    const mandatory = average('mandatory');
    const urgency = average('urgency');

    const score = popularity*0.25 + market*0.2 + frequency*0.2 + cost*0.15 + urgency*0.1 + mandatory*0.1
    return Math.round(score*100)
}

/** @molecule NavBar: */
export const NavBar = () => <div/>

/** @molecule TopIdeas: */
export const TopIdeas = () => <div/>


type inputSize = "is-small" | "is-normal" | "is-medium" | "is-large"

interface iInput { 
    value: string;
    setValue: Dispatch<SetStateAction<string>>;
    placeholder?: string,
    size?: inputSize
};

/** @atom Input. */
const Input = ({ value="", setValue, size="is-normal", placeholder="" }: iInput) => <input 
    value = { value }
    className={`input ${size || ''} is-link`}
    placeholder = { placeholder } 
    onChange={({target:{value}}) => setValue(value)}
/>


interface iInputProps { size?: inputSize; placeholder?: string; };  
interface iNewIdea {
    inputProps?: iInputProps;
    post(value: string):void;
    defaultValue?: string; 
    onClose():void
}

const NewIdea = ({ post, inputProps, defaultValue = "", onClose }: iNewIdea) => {
    const [ value, setValue ] = useState(defaultValue)

    return <div className="modal is-active">
        <div className="modal-background"></div>
            <div className="modal-card">
            <header className="modal-card-head">
                <p className="modal-card-title"> 
                    <strong style={{color:'rgb(0, 0, 180)'}}>Idea URL</strong> 
                </p>
                <button className="delete" aria-label="close" onClick={onClose}/>
            </header>
            <section className="modal-card-body">
                <div className="field has-addons" style={{width:"fit-content", margin:"auto"}}>
                    <div className="control">
                        <Input value={value} setValue={setValue} {...inputProps}/>
                    </div>
                    <div className="control">
                        <a className="button is-info" onClick={() => post(value)}>
                            Submit
                        </a>
                    </div>
                </div>
            </section>
        </div>
    </div>
}

type db = { app: string; service?: string; name: string; collection: string };


/** @page Home. */
export const Home = ({DB, ideas: seedIdeas}: {DB?: db, ideas?:Idea[] }) => {
    useEffect(() => { amplitude.getInstance().logEvent('NEW_VISIT') }, [])

    const [ idea, setIdea ] = useState<Idea | null>(null)
    const [ ideas, setIdeas ] = useState<Idea[]>(seedIdeas || [])
    const [ collection, setCollection ] = useState<RemoteMongoCollection<unknown>>()
    const [ viewDetail, setViewDetail ] = useState<boolean>(false)
    const [ viewNewIdea, setNewIdea ] = useState<boolean>(false)

    useEffect(() => { 
        const initDB = async() => {
            if(!DB) return
    
            const client = Stitch.initializeDefaultAppClient(DB.app)
            await client.auth.loginWithCredential(new AnonymousCredential())
        
            const db = client.getServiceClient(RemoteMongoClient.factory, DB.service || 'mongodb-atlas').db(DB.name)
            const dbCollection = db.collection(DB.collection)
            setCollection(dbCollection)

            const docs = await dbCollection.find({}, {limit: 100}).asArray().catch(console.log) as Idea[];
            setIdeas(docs);
        }
                
        initDB()
    }, [DB])

    const recommendIdea = (newIdea: string) => {
        amplitude.getInstance().logEvent('NEW_IDEA', {url: newIdea})
        setNewIdea(false)
    }


    const setDetail = (idea: Idea ) => {
        setIdea(idea)
        setViewDetail(true)

        const event = { idea: idea.name, score: idea.score, pitch: idea.pitch }
        amplitude.getInstance().logEvent('CLICK_IDEA', event)
    }

    const subtitle = <>
        Evaluate new business ideas, <br/> 
        get inspired for your next venture!
    </>

    return <div 
        className="section" 
        style={{backgroundColor: 'aliceblue', padding: '0rem 1.5rem 3rem'}}
    >
        <div className="container" id="submit-idea">
            <div className="columns">
                <div className="column">
                    <Text 
                        alignment={"right"}
                        content={
                            <a
                                id="submit-idea-link" 
                                onClick={() => setNewIdea(true)}
                            > Recommend an Idea </a>
                        }
                    />
                </div>
            </div>
        </div><br/>
        <Text content={"Startup Ideas"} isTitle alignment={"centered"} color={'link'} size={1} />
        <Text content={subtitle} isSubtitile alignment={"centered"}/>

        {  
            viewNewIdea 
                ?   <NewIdea post={(value)=> recommendIdea(value)} onClose={() => setNewIdea(false)}/> 
                :    null 
        } 

        <IdeasGrid 
            ideas={ideas.sort(({score: a}, {score: b}) => a > b ? -1 : 1)} 
            onClick={(idea) => 
            setDetail(idea)}
        />

        {
            viewDetail && idea
                ? 
                    <IdeaDetail 
                        collection={collection} 
                        idea={idea}
                        onClose={() => setViewDetail(false)}
                    />
                :   null
        }
    </div>
}
